import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { filter, map, skipWhile } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { CustomerFacade } from '../facades/customer.facade';
import { TermConstants } from '../shared/terms/TermConstants';
import { AuthorizationFacade } from '../facades/authorization.facade';

@Injectable({
  providedIn: 'root',
})
export class TermsGuard implements CanActivate {
  private readonly router: Router = inject(Router);
  private readonly authorizationFacade: AuthorizationFacade = inject(AuthorizationFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);

  canActivate(
    _next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean> {
    return combineLatest([
      this.authorizationFacade.isLoginInProcess$,
      this.authorizationFacade.isAuthenticated$,
      this.customerFacade.selectCustomerLoadedGuardData(),
    ]).pipe(
      skipWhile(([isLoginInProcess]: [boolean, boolean, any]): boolean => isLoginInProcess),
      filter(([, isAuthenticated, customerLoadedGuardData]: [boolean, boolean, any]): boolean => {
        if (!isAuthenticated) {
          return true;
        }
        if (customerLoadedGuardData.loadFailed) {
          return true;
        }
        return customerLoadedGuardData.consentedTerms !== undefined;
      }),
      map(([, isAuthenticated, customerLoadedGuardData]: [boolean, boolean, any]): boolean => {
        if (isAuthenticated && !customerLoadedGuardData.isGuest) {
          const termsOfUseKeys: {
            cmsKey: string;
            termsKey: string;
            urlKey: string | undefined
          } = TermConstants.termKeys.TERMS_OF_USE;

          if (!customerLoadedGuardData.consentedTerms.includes(termsOfUseKeys.termsKey)) {
            this.router.navigate(['terms', termsOfUseKeys.urlKey]).then();
            return false;
          }
        }

        return true;
      }),
    );
  }
}
